import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { useIsRestoring } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { API, auth, authHeaders } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { localStoragePersister, CACHE_BUSTER, MAX_AGE_MS } from "@/lib/queryPersister";
import Sidebar from "@/layout/Sidebar";
import Topbar from "@/layout/Topbar";
// import LiveRibbon from "@/layout/LiveRibbon"; // unmounted per user request — file kept for later re-mount
import CommandPalette from "@/layout/CommandPalette";
import ChatSurface from "@/layout/ChatSurface";
import { SystemActivityProvider } from "@/hooks/useSystemActivity";
import { EditorialProgressProvider } from "@/hooks/useEditorialProgress";
import EditorialProgressModal from "@/components/EditorialProgressModal";
import { BriefProgressProvider } from "@/hooks/useBriefProgress";
import {
  BriefProgressModal,
} from "@/components/BriefProgressModal";
import ProgressPillStack from "@/components/ProgressPillStack";

/*
 * Turn CC · 16 — RestoreGate blocks the app tree until the react-query
 * localStorage cache has been hydrated. On a warm session, restoration
 * is a synchronous localStorage read (~1-5 ms), so the gate is invisible
 * to the user. On a cold session there's no cache to restore, `useIsRestoring`
 * flips to false immediately, and the gate falls through instantly.
 *
 * The gate MUST sit INSIDE `PersistQueryClientProvider`, else
 * `useIsRestoring` cannot read the persist status and always returns
 * `false`.
 */
function RestoreGate({ children }) {
  const isRestoring = useIsRestoring();
  if (isRestoring) {
    return (
      <div
        className="min-h-screen bg-hm-bg flex items-center justify-center"
        data-testid="restore-gate"
      />
    );
  }
  return children;
}


export default function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * Turn CC · 14 — Instant boot for returning users.
   *
   * The auth-me check used to gate the entire tree behind a network
   * round-trip ("BOOTING OS…" screen for 500-1500 ms on every reload).
   * We now hydrate `user` synchronously from localStorage and drop
   * straight into the app; the `/auth/me` call runs in the background
   * and only redirects to /login if the session has actually expired.
   *
   * Storage key `hm_last_user` is a soft cache (username + email), not
   * an auth secret — the real session token lives in httpOnly cookie
   * or the `hm_session_token` LS key managed by auth/api.js. Wiping
   * `hm_last_user` cannot elevate privileges; it only forces the boot
   * screen back on the next load.
   */
  const _readLastUser = () => {
    try {
      const raw = localStorage.getItem("hm_last_user");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.id && parsed.email) return parsed;
      return null;
    } catch { return null; }
  };
  const _cached = location.state?.user || _readLastUser();
  const [user, setUser] = useState(_cached);
  // If we have a cached user, drop straight into the app; else show the
  // boot spinner while we call /auth/me for the first time.
  const [authState, setAuthState] = useState(_cached ? "authed" : "loading");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // One-time purge of legacy dark-mode state. Beyond Activ brand is light-only;
  // returning users who previously set hm_theme=dark are migrated on next mount.
  useEffect(() => {
    try {
      document.documentElement.removeAttribute("data-theme");
      document.documentElement.classList.remove("dark");
      localStorage.removeItem("hm_theme");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    // Turn CC · 14 — Always revalidate /auth/me on mount, but do so
    // silently in the background when we already have a cached user.
    // Only flip authState back to "unauth" if the server explicitly
    // rejects the session (401/403/expired) — transient errors keep
    // the cached user visible.
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { headers: authHeaders() });
        if (cancelled) return;
        if (res.status === 401 || res.status === 403) {
          // Real session expiration — clear cache and bounce to /login.
          try { localStorage.removeItem("hm_last_user"); } catch { /* noop */ }
          setAuthState("unauth");
          return;
        }
        if (!res.ok) return; // transient — keep whatever we have
        const u = await res.json();
        setUser(u);
        try {
          // Cache trimmed shape only — no session token, no secrets.
          localStorage.setItem(
            "hm_last_user",
            JSON.stringify({ id: u.id, email: u.email, name: u.name, picture: u.picture }),
          );
        } catch { /* quota / privacy mode */ }
        setAuthState("authed");
        // Turn CC · 16 — Warm the cache in background for the pages the
        // user is most likely to open. All queries share the persist
        // client so these results survive reloads.
        try {
          const { queryClient } = await import("@/lib/queryClient");
          const { fetchers, qk } = await import("@/lib/queryClient");
          queryClient.prefetchQuery({ queryKey: qk.dailyBriefs(),      queryFn: fetchers.dailyBriefs });
          queryClient.prefetchQuery({ queryKey: qk.weeklyBriefs(),     queryFn: fetchers.weeklyBriefs });
          queryClient.prefetchQuery({ queryKey: qk.deliverySettings(), queryFn: fetchers.deliverySettings });
          queryClient.prefetchQuery({ queryKey: qk.schedulerStatus(),  queryFn: fetchers.schedulerStatus });
          // Turn CC · 24 — Warm the dashboard Calendar + Kanban queries
          // so the module hydrates instantly from cache after boot. Uses
          // the same queryKey shape as the module hooks so the cached
          // entry is discovered on the very first render.
          try {
            const {
              calendarEventsQueryKey, calendarEventsQueryFn,
            } = await import("@/hooks/useCalendarEvents");
            const {
              kanbanPipelineQueryKey, kanbanPipelineQueryFn,
            } = await import("@/hooks/useKanbanPipeline");
            const now = new Date();
            const first = new Date(now.getFullYear(), now.getMonth(), 1);
            const from = new Date(first);
            from.setMonth(from.getMonth() - 1);
            const to = new Date(first);
            to.setMonth(to.getMonth() + 2);
            to.setDate(0);
            const ymd = (d) => {
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, "0");
              const dd = String(d.getDate()).padStart(2, "0");
              return `${y}-${m}-${dd}`;
            };
            const calArgs = { from: ymd(from), to: ymd(to), types: undefined };
            queryClient.prefetchQuery({
              queryKey: calendarEventsQueryKey(calArgs),
              queryFn: () => calendarEventsQueryFn(calArgs),
            });
            queryClient.prefetchQuery({
              queryKey: kanbanPipelineQueryKey({ days: 7 }),
              queryFn: () => kanbanPipelineQueryFn({ days: 7 }),
            });
          } catch { /* dashboard prefetch is best-effort */ }
        } catch { /* prefetch is best-effort */ }
      } catch {
        // Network error — if we had a cached user, keep it; otherwise flag unauth.
        if (!cancelled && !user) setAuthState("unauth");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async () => {
    await fetch(`${API}/auth/logout`, {
      method: "POST",
      headers: authHeaders(),
    }).catch(() => {});
    auth.clear();
    // Turn CC · 14 — clear cached user + react-query localStorage so a
    // new user logging in on the same device gets a clean slate.
    try {
      localStorage.removeItem("hm_last_user");
      localStorage.removeItem("hm-rq-cache");
    } catch { /* noop */ }
    navigate("/login", { replace: true });
  }, [navigate]);

  // Global shortcuts: ⌘K palette, ⌘J chat, Esc closes both
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      } else if (mod && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setChatOpen((v) => !v);
      } else if (e.key === "Escape") {
        setChatOpen(false);
        setPaletteOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (authState === "loading") {
    return (
      <div className="min-h-screen bg-hm-bg flex items-center justify-center mono text-[11px] uppercase tracking-[0.22em] text-hm-fg-subtle">
        booting os…
      </div>
    );
  }
  if (authState === "unauth") {
    return <Navigate to="/login" replace />;
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: localStoragePersister,
        buster: CACHE_BUSTER,
        maxAge: MAX_AGE_MS,
        dehydrateOptions: {
          // Persist only successful queries — no in-flight or errored state
          // so a transient 5xx never poisons localStorage.
          shouldDehydrateQuery: (q) => q.state.status === "success",
        },
      }}
    >
      <RestoreGate>
        <SystemActivityProvider>
          <EditorialProgressProvider>
          <BriefProgressProvider>
            <div className="h-screen bg-hm-bg text-hm-fg flex overflow-hidden hm-noise" data-testid="app-shell">
              <Sidebar
                user={user}
                onCommandPalette={() => setPaletteOpen(true)}
                onLogout={logout}
              />
              <div className="flex-1 min-w-0 flex flex-col relative">
                <Topbar user={user} onLogout={logout} />
                <main className="flex-1 min-h-0 overflow-y-auto" data-testid="page-content">
                  <div className="max-w-[1240px] mx-auto px-8 py-8 pb-40">{children}</div>
                </main>
              </div>
              <CommandPalette
                open={paletteOpen}
                onOpenChange={setPaletteOpen}
                onToggleChat={setChatOpen}
                onLogout={logout}
              />
              <ChatSurface open={chatOpen} onOpenChange={setChatOpen} />
              <BriefProgressModal />
              <EditorialProgressModal />
              {/* Persistent picture-in-picture pills for minimized brief +
                  editorial runs. Rendered outside the modal DOM so they
                  survive navigation between pages. Must sit INSIDE both
                  progress providers so it can read their state. */}
              <ProgressPillStack />
            </div>
          </BriefProgressProvider>
          </EditorialProgressProvider>
        </SystemActivityProvider>
      </RestoreGate>
      {import.meta.env.DEV ? (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      ) : null}
    </PersistQueryClientProvider>
  );
}
